export const PMS_THEME_STORAGE_KEY = "palantir_pms_theme";
export const PMS_DEFAULT_THEME_ID = "light";
const PMS_THEME_BY_USER_STORAGE_KEY = "palantir_pms_theme_by_user";
const PMS_LIGHT_DEFAULT_MIGRATION_KEY = "palantir_pms_light_default_v1";

const themes = [
  {
    id: "light",
    label: "Pilcore Day",
    description: "Blue structure and clean white surfaces",
    colorScheme: "light",
    preview: {
      shell: "#f4f8fb",
      sidebar: "#223a7a",
      sidebarAccent: "#3db6e9",
      surface: "#ffffff",
      surfaceAlt: "#f5f9fc",
      border: "#d7e2ec",
      text: "#172653",
      muted: "#66718d",
      highlight: "#3db6e9",
      success: "#63c1c7",
    },
  },
  {
    id: "dark",
    label: "Pilcore Night",
    description: "Deep Pilcore workspace",
    colorScheme: "dark",
    preview: {
      shell: "#101a36",
      sidebar: "#223a7a",
      sidebarAccent: "#3db6e9",
      surface: "#172653",
      surfaceAlt: "#122044",
      border: "#36599b",
      text: "#ffffff",
      muted: "#b8c5dd",
      highlight: "#3db6e9",
      success: "#63c1c7",
    },
  },
];

export const PMS_THEME_OPTIONS = Object.freeze(themes);

const themeMap = themes.reduce((acc, theme) => {
  acc[theme.id] = theme;
  return acc;
}, {});

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function getUserPreferenceMap() {
  if (typeof window === "undefined") return {};

  try {
    const raw = JSON.parse(localStorage.getItem(PMS_THEME_BY_USER_STORAGE_KEY) || "{}");
    return isPlainObject(raw) ? raw : {};
  } catch (_err) {
    return {};
  }
}

function setUserPreferenceMap(nextMap) {
  if (typeof window === "undefined") return;

  try {
    localStorage.setItem(PMS_THEME_BY_USER_STORAGE_KEY, JSON.stringify(nextMap));
  } catch (_err) {
    // Ignore storage failures and keep the theme in memory only.
  }
}

function getUserCacheKey(user) {
  const rawUserId = user?.id;
  if (rawUserId == null) return "";
  return String(rawUserId).trim();
}

export function isKnownPmsThemeId(themeId) {
  return Boolean(themeMap[String(themeId || "")]);
}

export function getPmsThemeDefinition(themeId) {
  return themeMap[String(themeId || "")] || themeMap[PMS_DEFAULT_THEME_ID];
}

export function readPmsThemeIdFromUser(user) {
  const preferences = isPlainObject(user?.preferences) ? user.preferences : {};
  const uiPreferences = isPlainObject(user?.ui_preferences) ? user.ui_preferences : {};
  const candidates = [
    user?.pms_theme_id,
    preferences.pms_theme_id,
    preferences.pmsThemeId,
    uiPreferences.pms_theme_id,
    uiPreferences.pmsThemeId,
  ];

  const knownThemeId = candidates.find((candidate) => isKnownPmsThemeId(candidate));
  return knownThemeId || "";
}

export function storePmsThemeId(themeId, user = null) {
  const safeThemeId = isKnownPmsThemeId(themeId) ? String(themeId) : PMS_DEFAULT_THEME_ID;
  if (typeof window === "undefined") return safeThemeId;

  try {
    localStorage.setItem(PMS_THEME_STORAGE_KEY, safeThemeId);
  } catch (_err) {
    // Ignore storage failures and keep the theme in memory only.
  }

  const userKey = getUserCacheKey(user);
  if (userKey) {
    const currentMap = getUserPreferenceMap();
    setUserPreferenceMap({
      ...currentMap,
      [userKey]: safeThemeId,
    });
  }

  return safeThemeId;
}

export function readStoredPmsThemeId(user = null) {
  const userThemeId = readPmsThemeIdFromUser(user);
  if (typeof window === "undefined") return userThemeId || PMS_DEFAULT_THEME_ID;

  let shouldApplyLightDefault = false;
  try {
    shouldApplyLightDefault = localStorage.getItem(PMS_LIGHT_DEFAULT_MIGRATION_KEY) !== "done";
    if (shouldApplyLightDefault) {
      localStorage.setItem(PMS_LIGHT_DEFAULT_MIGRATION_KEY, "done");
    }
  } catch (_err) {
    // Storage may be unavailable; the in-memory default still remains light.
  }

  // A server-backed user choice is explicit and always wins. The one-time
  // migration only replaces legacy local dark values that were auto-stored.
  if (userThemeId) return userThemeId;
  if (shouldApplyLightDefault) return PMS_DEFAULT_THEME_ID;

  const userKey = getUserCacheKey(user);
  if (userKey) {
    const userMap = getUserPreferenceMap();
    return isKnownPmsThemeId(userMap[userKey]) ? userMap[userKey] : PMS_DEFAULT_THEME_ID;
  }

  try {
    const storedThemeId = localStorage.getItem(PMS_THEME_STORAGE_KEY);
    return isKnownPmsThemeId(storedThemeId) ? storedThemeId : PMS_DEFAULT_THEME_ID;
  } catch (_err) {
    return PMS_DEFAULT_THEME_ID;
  }
}
